import React from 'react';
import { parseServerTime } from '../utils/serverTime';

/**
 * Hold indicator for an arrival row (Predictions table).
 *
 * The backend (HoldOutlook) sets holdState on each getPredictionsForDate row:
 *   HOLDING — in a hold now; holdEnteredAt gives the elapsed time (ticks live here)
 *   HELD    — held earlier on this approach; holdMinutes = duration (null if no exit recorded)
 *   LIKELY  — due within 30 min while the destination's stack is active (someone holding, a
 *             hold just ended, a go-around in the last 30 min, or 14+ flights due in 30 min);
 *             holdMinutes = expected hold, holdStackCount = aircraft holding now,
 *             holdLikelyReason / holdDueNext30 = why
 * Live prediction_update events carry inHold / holdEnteredAt / holdLoops and are folded in by
 * mergeLiveHold so a hold shows up without waiting for the next REST refresh.
 *
 * Each state has its own glyph as well as colour — the Mono theme is greyscale.
 */

const GLYPH = { HOLDING: '●', HELD: '◆', LIKELY: '◌' };
const LABEL = { HOLDING: 'Holding', HELD: 'Held', LIKELY: 'Likely' };

// Sort rank: what needs attention first when sorted descending.
const RANK = { HOLDING: 3, LIKELY: 2, HELD: 1 };

export function holdRank(row) {
  return row && row.holdState ? RANK[row.holdState] || 0 : 0;
}

function fmtMin(m) {
  if (m == null || !Number.isFinite(m)) return null;
  return `${Math.max(0, Math.round(m))}m`;
}

/** Circuits of the hold, to one decimal: "1 loop", "2.5 loops". */
export function fmtLoops(l) {
  if (l == null || !Number.isFinite(l) || l <= 0) return null;
  const n = Math.round(l * 10) / 10;
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)} ${n === 1 ? 'loop' : 'loops'}`;
}

/**
 * Minutes to show: live elapsed for HOLDING (from entry, against the page clock),
 * otherwise the backend's figure.
 */
export function holdMinutesNow(row, nowMs) {
  if (!row || !row.holdState) return null;
  if (row.holdState === 'HOLDING' && row.holdEnteredAt) {
    const t = parseServerTime(row.holdEnteredAt);
    if (t) return (nowMs - t.getTime()) / 60000;
  }
  return row.holdMinutes != null ? row.holdMinutes : null;
}

const LIKELY_SHORT = { GO_AROUND: 'after go-around', DEMAND: 'busy' };

/** Why a flight is marked LIKELY (HoldOutlook.Reason on the server). */
export function likelyWhy(row) {
  const due = row.holdDueNext30 != null ? `${row.holdDueNext30} flights due in 30 min` : null;
  switch (row.holdLikelyReason) {
    case 'RECENT_HOLD': return 'a hold at the destination ended in the last 10 min';
    case 'GO_AROUND': return 'a go-around at the destination in the last 30 min';
    case 'DEMAND': return `${due || '14+ flights due in 30 min'}, above what usually lands without holding`;
    default: return `${row.holdStackCount || 0} aircraft holding at the destination now`;
  }
}

/** The two lines of the hold cell, plus a tooltip. Null when there is nothing to show. */
export function holdView(row, nowMs) {
  if (!row || !RANK[row.holdState]) return null;
  const state = row.holdState;
  const mins = fmtMin(holdMinutesNow(row, nowMs));
  const loops = fmtLoops(row.holdLoops);
  let line2;
  if (state === 'LIKELY') {
    line2 = mins ? `~${mins} expected` : (LIKELY_SHORT[row.holdLikelyReason] || 'stack active');
  } else {
    line2 = [mins || (state === 'HELD' ? 'duration n/a' : null), loops].filter(Boolean).join(' · ');
  }
  const tip = [];
  if (state === 'HOLDING') tip.push(`In a hold${mins ? ` for ${mins}` : ''}${loops ? `, ${loops} so far` : ''}`);
  if (state === 'HELD') tip.push(`Held${mins ? ` for ${mins}` : ''}${loops ? ` (${loops})` : ''} on this approach`);
  if (state === 'LIKELY') {
    tip.push(`Likely to hold: ${likelyWhy(row)}`
      + (mins ? `; expected hold about ${mins} (median of the current stack and the last hour)` : ''));
    tip.push('Historically about 1 in 4 of these is followed by a hold within 30 min (6.6 % otherwise).');
  }
  if (row.holdPatternName) tip.push(`Pattern: ${row.holdPatternName}`);
  if (row.holdEntryAltitudeFt != null) {
    tip.push(`Entry ${row.holdEntryAltitudeFt} ft`
      + (row.holdExitAltitudeFt != null ? ` → exit ${row.holdExitAltitudeFt} ft` : ''));
  }
  if (state === 'HOLDING' && row.holdStackCount > 1) tip.push(`${row.holdStackCount} aircraft in the stack`);
  return { state, glyph: GLYPH[state], label: LABEL[state], line2, title: tip.join('\n') };
}

/**
 * Fold a live prediction event's hold flags into a row. A live inHold=true makes it
 * HOLDING; inHold=false on a row that was HOLDING makes it HELD with the duration so
 * far. A LIKELY / HELD verdict from the server is otherwise left alone.
 */
export function mergeLiveHold(row, evt) {
  if (!evt || evt.inHold == null) return row;
  if (evt.inHold) {
    return {
      ...row,
      holdState: 'HOLDING',
      holdEnteredAt: evt.holdEnteredAt || row.holdEnteredAt,
      holdEntryAltitudeFt: evt.holdEntryAltitudeFt != null ? evt.holdEntryAltitudeFt : row.holdEntryAltitudeFt,
      holdLoops: evt.holdLoops != null ? evt.holdLoops : row.holdLoops,
      holdExitedAt: null,
    };
  }
  if (row.holdState === 'HOLDING') {
    const entered = row.holdEnteredAt ? parseServerTime(row.holdEnteredAt) : null;
    const exited = evt.predictionAt ? parseServerTime(evt.predictionAt) : null;
    return {
      ...row,
      holdState: 'HELD',
      holdExitedAt: evt.predictionAt || null,
      holdLoops: evt.holdLoops != null ? evt.holdLoops : row.holdLoops,
      holdMinutes: entered && exited ? (exited.getTime() - entered.getTime()) / 60000 : row.holdMinutes,
    };
  }
  return row;
}

/** Two-line hold cell content: badge, then duration / expectation. */
export function HoldCellContent({ row, nowMs }) {
  const v = holdView(row, nowMs);
  if (!v) {
    return (
      <>
        <div className="pt-l1"><span className="pt-muted">—</span></div>
        <div className="pt-l2">&nbsp;</div>
      </>
    );
  }
  return (
    <>
      <div className="pt-l1" title={v.title}>
        <span className={`hold-badge hold-${v.state.toLowerCase()}`}>
          <span className="hold-glyph" aria-hidden="true">{v.glyph}</span> {v.label}
        </span>
      </div>
      <div className="pt-l2" title={v.title}>{v.line2 || ' '}</div>
    </>
  );
}
